<template>
  <div class="border rounded-md p-4 bg-white">
    <!-- Loading State -->
    <template v-if="loading">
      <ui-loader />
      <p class="text-sm text-gray-500 mt-2 text-center">
        Loading contract information...
      </p>
    </template>

    <!-- Error State -->
    <template v-else-if="error">
      <div class="text-red-500 text-center py-4">
        <i class="pi pi-exclamation-circle text-2xl mb-2"></i>
        <p>Error loading contract information.</p>
        <p v-if="errorInfo" class="text-sm text-red-400 mt-1">
          {{ errorInfo }}
        </p>
      </div>
    </template>

    <!-- No Contract Selected -->
    <template v-else-if="!contractId">
      <div class="text-gray-500 text-center py-4">
        <i class="pi pi-file text-2xl mb-2"></i>
        <p>No contract selected.</p>
      </div>
    </template>

    <!-- Contract Data Display -->
    <template v-else-if="contractInfo">
      <!-- Header with Toggle -->
      <div
        class="flex items-center justify-between cursor-pointer select-none"
        @click="expanded = !expanded"
      >
        <div class="flex items-center gap-2">
          <i
            :class="[
              'pi text-gray-500',
              expanded ? 'pi-chevron-down' : 'pi-chevron-right',
            ]"
          ></i>
          <div>
            <h3 class="text-base font-semibold text-gray-900">
              {{ headerName }}
            </h3>
            <p class="text-xs text-gray-500">
              {{ contractInfo.UnitReference }}
            </p>
          </div>
        </div>
        <div class="flex flex-wrap gap-1.5">
          <span
            :class="[
              'px-2 py-0.5 text-xs font-medium rounded-full',
              contractInfo.InArrears
                ? 'bg-red-100 text-red-700'
                : 'bg-primary-100 text-primary-700',
            ]"
          >
            {{ contractInfo.InArrears ? "In Arrears" : "Paid Up" }}
          </span>
          <span
            v-if="contractInfo.DepositDue"
            class="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700"
          >
            Deposit Due
          </span>
          <span
            v-if="!contractInfo.DepositDue"
            class="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700"
          >
            Deposit Paid
          </span>
        </div>
      </div>

      <!-- Collapsed Summary -->
      <div class="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <div class="flex justify-between">
          <span class="text-gray-500">Monthly Rent:</span>
          <span class="font-medium">{{
            formatCurrency(contractInfo.ContractRent)
          }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-gray-500">Occupation:</span>
          <span>{{ formatDate(contractInfo.OccupationDate) }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-gray-500">Contract Expiry:</span>
          <span>{{ formatDate(contractInfo.LeaseExpiryDate) }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-gray-500">Deposit Amount:</span>
          <span>{{ formatCurrency(contractInfo.ContractFixedDeposit) }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-gray-500"
            >Deposit Due:
            {{ formatCurrency(contractInfo.DepositBalanceOutstanding) }}</span
          >
          <span
            :class="
              contractInfo.DepositDue ? 'text-yellow-600 font-medium' : ''
            "
          >
            {{ contractInfo.DepositDue ? "Due" : "Paid" }}
          </span>
        </div>
        <div class="flex justify-between">
          <span class="text-gray-500">Balance:</span>
          <span
            :class="
              contractInfo.TenantBalance < 0 ? 'text-red-600 font-medium' : ''
            "
          >
            {{ formatCurrency(contractInfo.TenantBalance) }}
          </span>
        </div>
      </div>

      <!-- Expanded Details -->
      <template v-if="expanded">
        <div class="mt-4 pt-3 border-t space-y-4">
          <!-- Key Dates -->
          <div>
            <h4 class="text-sm font-semibold text-gray-700 mb-2">Key Dates</h4>
            <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <div class="flex justify-between">
                <span class="text-gray-500">Mandate:</span>
                <span>{{ formatDate(contractInfo.MandateDate) }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">Mandate End:</span>
                <span>{{ formatDate(contractInfo.MandateEndDate) }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">Next Increase:</span>
                <span>{{ formatDate(contractInfo.NextIncreaseDate) }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">Admin on Rent:</span>
                <span>{{ contractInfo.AdminOnRentInd ? "Yes" : "No" }}</span>
              </div>
            </div>
          </div>

          <!-- Owner & Tenant -->
          <div
            :class="[
              'grid gap-4',
              showOwner && showTenant ? 'grid-cols-2' : 'grid-cols-1',
            ]"
          >
            <div v-if="showOwner">
              <h4 class="text-sm font-semibold text-gray-700 mb-2">Owner</h4>
              <div class="text-sm space-y-0.5">
                <p class="font-medium">{{ contractInfo.OwnerFullName }}</p>
                <p class="text-gray-500 text-xs">
                  Rekno: {{ contractInfo.OwnerRekno }}
                </p>
                <p
                  v-if="contractInfo.OwnerEMailAddress"
                  class="text-gray-600 truncate text-xs"
                  :title="contractInfo.OwnerEMailAddress"
                >
                  {{ contractInfo.OwnerEMailAddress }}
                </p>
              </div>
            </div>
            <div v-if="showTenant">
              <h4 class="text-sm font-semibold text-gray-700 mb-2">Tenant</h4>
              <div class="text-sm space-y-0.5">
                <p class="font-medium">{{ contractInfo.TenantFullName }}</p>
                <p class="text-gray-500 text-xs">
                  Rekno: {{ contractInfo.TenantRekno }}
                </p>
                <p
                  v-if="contractInfo.TenantEMailAddress"
                  class="text-gray-600 truncate text-xs"
                >
                  {{ contractInfo.TenantEMailAddress }}
                </p>
                <p
                  v-if="contractInfo.TenantCellPhoneNumber"
                  class="text-gray-600 text-xs"
                >
                  {{ contractInfo.TenantCellPhoneNumber }}
                </p>
              </div>
            </div>
          </div>

          <!-- Banking Details -->
          <div>
            <h4 class="text-sm font-semibold text-gray-700 mb-2">
              Banking Details
            </h4>
            <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <div class="flex justify-between">
                <span class="text-gray-500">Bank:</span>
                <span>{{ contractInfo.TenantBankDescription || "-" }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">Branch:</span>
                <span>{{ contractInfo.TenantBankBranchCode || "-" }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">Account:</span>
                <span>{{ contractInfo.TenantBankAccountNumber || "-" }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">Type:</span>
                <span>{{
                  contractInfo.TenantBankAccountTypeDescription || "-"
                }}</span>
              </div>
              <div class="flex justify-between col-span-2">
                <span class="text-gray-500">Debit Order Day:</span>
                <span>{{
                  contractInfo.TenantDebitOrderDayOfMonth || "-"
                }}</span>
              </div>
            </div>
          </div>

          <!-- Property Details -->
          <div>
            <h4 class="text-sm font-semibold text-gray-700 mb-2">Property</h4>
            <div class="text-sm space-y-1">
              <p>{{ contractInfo.ComplexName }}</p>
              <p class="text-gray-600 whitespace-pre-line text-xs">
                {{ contractInfo.UnitAddress }}
              </p>
              <p
                v-if="contractInfo.ShortDescription?.trim()"
                class="text-gray-500 italic text-xs"
              >
                {{ contractInfo.ShortDescription }}
              </p>
            </div>
          </div>

          <!-- Last Communication -->
          <div v-if="contractInfo.LastCommsStatus">
            <h4 class="text-sm font-semibold text-gray-700 mb-2">
              Last Communication
            </h4>
            <div class="text-sm flex items-center gap-2">
              <span
                :class="[
                  'px-2 py-0.5 text-xs font-medium rounded-full',
                  contractInfo.LastCommsStatus === 'Failed'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-blue-100 text-blue-700',
                ]"
              >
                {{ contractInfo.LastCommsType }}:
                {{ contractInfo.LastCommsStatus }}
              </span>
              <span class="text-gray-500 text-xs">{{
                formatDate(contractInfo.LastCommsCreationDate)
              }}</span>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    inputType?: string;
    error?: string;
    loading?: boolean;
    id?: string;
    options?: any[];
    fetchOptions?: boolean;
    auxInputType?: "tenant" | "owner" | "both";
    editable?: boolean;
    endpoint?: string;
    displayKey?: string;
    searchType?: string;
    filterQueryType?: "normal" | "serialized" | "both";
    valueKey?: string;
    responseKey?: string;
    limit?: number;
    filterEnabled?: boolean;
    filter?: { [key: string]: any };
    tokenData?: { [key: string]: any };
    placeholder?: string;
    defaultExpression?: string;
    dataKey?: string;
    affectFields?: string[];
    showAllOptionInSelect?: boolean;
    required?: boolean;
  }>(),
  {
    disabled: false,
    editable: false,
    filterQueryType: "normal",
    dataKey: Math.random().toString().substring(2, 20),
    loading: false,
    fetchOptions: false,
    responseKey: "Results",
    displayKey: "Name",
    valueKey: "Value",
    auxInputType: "tenant",
    limit: 40,
    filterEnabled: true,
    tokenData: () => ({}),
    filter: () => ({}),
    affectFields: () => [],
  },
);

const value = defineModel<any>("value");
const form = defineModel<any>("form", {
  default: () => ({}),
});
// Display options
const loading = ref<boolean>(false);
const error = ref<boolean>(false);
const errorInfo = ref<string>("");
const expanded = ref<boolean>(false);

// Contract stuff
const contractInfo = ref<any>(null);

// Display helpers based on auxInputType
const showTenant = computed(
  () => props.auxInputType === "tenant" || props.auxInputType === "both",
);
const showOwner = computed(
  () => props.auxInputType === "owner" || props.auxInputType === "both",
);

const headerName = computed(() => {
  if (!contractInfo.value) return "";
  if (props.auxInputType === "owner") return contractInfo.value.OwnerFullName;
  return contractInfo.value.TenantFullName;
});

// Formatting functions
function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "-";
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
  }).format(amount);
}

function formatDate(date: string | null | undefined): string {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-ZA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
const contractId = computed({
  get: () => {
    return form.value?.ContractId || null;
  },
  set: (val: any) => {
    form.value = {
      ...form.value,
      ContractId: val,
    };
  },
});

watch(
  () => contractId.value,
  async (newVal, oldVal) => {
    if (newVal !== oldVal && newVal != null) {
      await fetchContractInfo();
    }
  },
  { immediate: true },
);

async function fetchContractInfo() {
  loading.value = true;
  error.value = false;
  errorInfo.value = "";
  try {
    if (contractId.value) {
      const res = await $fetch(props.endpoint ?? "", {
        query: {
          ContractId: contractId.value,
        },
      });

      contractInfo.value = res.Results?.[0] || null;
    } else {
      contractInfo.value = null;
    }
  } catch (err) {
    error.value = true;
    errorInfo.value = (err as Error).message;
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped></style>
